import type { FC } from 'react';

import { Placeholder, useCms, useContent, useImageMapper, useRendering, useSetting } from '@eneco/cms';
import type { CmsRendering } from '@eneco/cms-adapter';
import { richTextByRole } from '@eneco/cms-sanity';
import RichText from '@eneco/components/RichText/RichText';
import { Bleed, Box, ButtonLink, Card, Heading, Image, Ribbon, Stack, Text, VisuallyHidden } from '@eneco/sparky';
import type { BoxProps, TextProps } from '@eneco/sparky/types';

import type { HeroCardContent } from './hero-card-content.types';
import { HERO_CARD_SLOTS, resolveSlot } from './slots';

/** Stable skip target for SkipToMain. Replace with CMS field when available. */
export const HERO_CARD_SKIP_TARGET_ID = 'hero-card';

/**
 * Fallback until AddressFinder exposes `accessibilityIntro` in the CMS.
 * Never shown unless AddressFinder is nested in this card.
 */
const ADDRESS_FINDER_ACCESSIBILITY_INTRO_FALLBACK =
  'Je bent op de pagina om je energiekosten te berekenen. Vul je postcode en huisnummer in. We gebruiken je adres om een persoonlijk aanbod te tonen.';

const ADDRESS_FINDER_COMPONENT_NAME = 'AddressFinder';

const getAddressFinderAccessibilityIntro = (rendering: CmsRendering | undefined): string | undefined => {
  const nested = [...HERO_CARD_SLOTS.top, ...HERO_CARD_SLOTS.main].flatMap(name => rendering?.placeholders?.[name] ?? []);
  const addressFinder = nested.find(item => item.componentName === ADDRESS_FINDER_COMPONENT_NAME);
  if (!addressFinder) return undefined;

  const intro = (addressFinder.content as { accessibilityIntro?: string } | undefined)?.accessibilityIntro?.trim();
  return intro || ADDRESS_FINDER_ACCESSIBILITY_INTRO_FALLBACK;
};

/**
 * `HeroCard` block for the CMS-agnostic lane, serving **both** providers.
 *
 * `buttonLink` carries its own display text on stored content, even though the generated schema does
 * not declare it (the dataset is schemaless). `uspList` is an unresolved reference stub with no
 * usable label, so it stays omitted. `jss-hero-card-top` has no Sanity-side field at all (only
 * `jss-hero-card-main`/`heroCardMain` is modeled), so that slot always renders empty there.
 */
const HeroCard: FC = () => {
  const { adapter } = useCms();
  const content = useContent<HeroCardContent>();
  const rendering = useRendering();
  const mapImage = useImageMapper();

  const footerBackgroundColor = useSetting('footerBackgroundColor') as BoxProps['backgroundColor'];
  const footerTextColor = useSetting('footerTextColor') as TextProps['color'];

  const primaryImage = mapImage(content.brandPrimaryImage);
  const secondaryImage = mapImage(content.brandSecondaryImage);
  const hasBrandImages = Boolean(primaryImage?.src || secondaryImage?.src);

  const richText = adapter.toRichText(content.content);
  // ADR-0068 swept `ribbonContent` into the shared `richText` family array, keyed by `role`.
  const ribbonText = adapter.toRichText(richTextByRole(content.richText, 'ribbonContent'));

  const cta = adapter.toLink(content.buttonLink);
  const topSlot = resolveSlot(rendering, HERO_CARD_SLOTS.top);
  const mainSlot = resolveSlot(rendering, HERO_CARD_SLOTS.main);
  const accessibilityIntro = getAddressFinderAccessibilityIntro(rendering);

  return (
    <div id={HERO_CARD_SKIP_TARGET_ID} tabIndex={-1}>
      {accessibilityIntro ? <VisuallyHidden>{accessibilityIntro}</VisuallyHidden> : null}
      <Card overflow={hasBrandImages ? undefined : 'hidden'}>
        {hasBrandImages && (
          <Stack direction="row" alignX="end">
            <Bleed top="10">
              <Stack direction="row" gap={1}>
                {primaryImage?.src && (
                  <Image
                    src={primaryImage.src}
                    alt=""
                    width="90px"
                    height="90px"
                    display="block"
                    objectFit="contain"
                  />
                )}
                {secondaryImage?.src && (
                  <Image
                    src={secondaryImage.src}
                    alt=""
                    width="90px"
                    height="90px"
                    display="block"
                    objectFit="contain"
                  />
                )}
              </Stack>
            </Bleed>
          </Stack>
        )}
        {ribbonText && (
          <Ribbon size="large">
            <RichText html={ribbonText} />
          </Ribbon>
        )}
        <Box padding={{ initial: '6', md: '10' }} {...(hasBrandImages && { paddingTop: { initial: '6', md: '0' } })}>
          <Stack gap="6">
            {content.title && (
              <Heading size={{ initial: 'S', md: 'L' }} as="h1">
                {content.title}
              </Heading>
            )}

            {topSlot ? (
              <Box>
                <Placeholder name={topSlot} />
              </Box>
            ) : null}

            {richText && (
              <Bleed top="2">
                <Text size="BodyL">
                  <RichText html={richText} />
                </Text>
              </Bleed>
            )}

            {mainSlot ? (
              <Box>
                <Placeholder name={mainSlot} />
              </Box>
            ) : null}

            {cta?.text && (
              <Stack.Item grow={false}>
                <ButtonLink href={cta.href} target={cta.target}>
                  {cta.text}
                </ButtonLink>
              </Stack.Item>
            )}
          </Stack>
        </Box>
        {content.footerText && (
          <Box
            backgroundColor={footerBackgroundColor}
            borderBottomLeftRadius="m"
            borderBottomRightRadius="m"
            paddingX={{ initial: '6', md: '10' }}
            paddingY="3">
            <Stack alignX="center">
              <Text size="BodyS" display="inline" weight="bold" color={footerTextColor}>
                {content.footerText}
              </Text>
            </Stack>
          </Box>
        )}
      </Card>
    </div>
  );
};

export default HeroCard;
